import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'; 
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from "react-chartjs-2";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

export const ProfileVisualization = ({ 
  points = [], 
  smoothedPoints = [],
  curves = [],
  annotations = [],
  clearanceIssues,
  onAnnotationAdd,
  onAnnotationUpdate,
  onAnnotationDelete,
  isRecording 
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current && isRecording) {
      chartRef.current.update();
    }
  }, [points, isRecording]);

  const data = {
    labels: points.map(p => Math.round(p.distance)),
    datasets: [
      // Raw elevation data
      {
        label: 'Raw Elevation',
        data: points.map(p => p.elevation),
        fill: false,
        borderColor: '#3B82F6',
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 4
      },
      // Smoothed elevation data
      {
        label: 'Smoothed Elevation',
        data: smoothedPoints.map(p => p.elevation),
        fill: false,
        borderColor: '#10B981',
        tension: 0.4,
        pointRadius: 0,
        borderDash: [5, 5]
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 20 // Add padding at bottom of chart
      }
    },
    backgroundColor: 'white',
    animation: {
      duration: isRecording ? 0 : 1000
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#374151'
        }
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const dataset = context.dataset.label;
            const value = context.parsed.y.toFixed(1);
            return `${dataset}: ${value}m`;
          },
          title: (context) => {
            return `Distance: ${context[0].label}m`;
          },
          afterBody: (context) => {
            const point = points[context[0].dataIndex];
            return point?.precision ? [`Recording precision: ${point.precision}m`] : [];
          }
        }
      },
      annotation: {
        annotations: [
          // Add turning radius annotations
          ...curves.map((curve, i) => ({
            type: 'line',
            xMin: points[curve.startIndex].distance,
            xMax: points[curve.endIndex].distance,
            borderColor: curve.isCritical ? '#EF4444' : curve.type === 'sharp' ? '#F59E0B' : '#10B981',
            borderWidth: 2,
            label: {
              content: `${curve.type} curve (R=${curve.radius.toFixed(1)}m${
                curve.minRadius ? `, min=${curve.minRadius.toFixed(1)}m` : ''
              })${curve.isCritical ? ' ⚠️ TIGHT TURN' : ''}${
                curve.details ? `\nRadius: ${curve.details.radius.toFixed(1)}m (${curve.details.percentageOfMin?.toFixed(0)}% of min)` : ''
              }`,
              enabled: true,
              position: 'top',
              backgroundColor: curve.isCritical ? '#EF4444' : curve.type === 'sharp' ? '#F59E0B' : '#10B981',
              color: '#FFFFFF',
              padding: 6,
              font: {
                size: 11,
                weight: curve.isCritical ? 'bold' : 'normal'
              }
            }
          })),
          // Add clearance issue annotations
          ...clearanceIssues.map((issue, i) => ({
            type: 'line',
            xMin: issue.distance,
            xMax: issue.distance,
            borderColor: issue.severity === 'critical' ? '#EF4444' : '#F59E0B',
            borderWidth: 2,
            label: {
              content: issue.message,
              enabled: true,
              position: 'top',
              backgroundColor: issue.severity === 'critical' ? '#EF4444' : '#F59E0B'
            }
          })),
          // Add curve annotations
          ...curves.map((curve, i) => ({
            type: 'line',
            xMin: points[curve.startIndex].distance,
            xMax: points[curve.endIndex].distance,
            borderColor: curve.type === 'sharp' ? '#EF4444' : '#F59E0B',
            borderWidth: 2,
            label: {
              content: `${curve.type} curve (R=${curve.radius.toFixed(1)}m)`,
              enabled: true,
              position: 'top'
            }
          })),
          // Add user annotations
          ...annotations.map((annotation, i) => ({
            type: 'label',
            xValue: annotation.distance,
            yValue: annotation.elevation,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            content: annotation.text,
            font: {
              size: 12
            },
            onClick: function(e) {
              const newText = prompt('Edit annotation:', annotation.text);
              if (newText !== null) {
                onAnnotationUpdate(i, {
                  ...annotation,
                  text: newText
                });
              }
            }
          }))
        ]
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Distance (m)',
          color: '#374151'
        },
        grid: {
          color: '#E5E7EB'
        },
        ticks: {
          color: '#374151'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Elevation (m)',
          color: '#374151'
        },
        grid: {
          color: '#E5E7EB'
        },
        ticks: {
          color: '#374151'
        }
      }
    }
  };

  const handleChartClick = (event) => {
    if (!event.native) return;

    const chart = chartRef.current;
    const rect = chart.canvas.getBoundingClientRect();
    const x = event.native.clientX - rect.left;
    const y = event.native.clientY - rect.top;
    
    const xValue = chart.scales.x.getValueForPixel(x);
    const yValue = chart.scales.y.getValueForPixel(y);

    const text = prompt('Enter annotation text:');
    if (text) {
      onAnnotationAdd({
        distance: xValue,
        elevation: yValue,
        text
      });
    }
  };

  return (
    <div className="h-[452px] relative bg-white rounded-lg">
      {points.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center text-gray-600">
          No profile data recorded yet
        </div>
      ) : (
        <Line 
          ref={chartRef} 
          data={data} 
          options={options} 
          onClick={handleChartClick}
        />
      )}
    </div>
  );
};